<script lang="ts" setup>
/**
 * Luma's reasoning, streamed as immutable bubbles.
 *
 * Each beat of work lands as its own row and never changes again — the trail
 * reads as a record of what Luma did rather than one status line overwriting
 * itself. While the answer is still coming, the newest row is the live one and
 * carries the shimmer and the Stop control; everything above it has settled.
 *
 * Once the answer arrives the trail collapses behind a one-line summary so the
 * answer stays the thing you read, with the reasoning a click away. It is
 * session-only state, so it is gone after a reload — the summary says as much
 * by counting steps rather than promising a permanent record.
 */
import type { LumaThought } from '@/types/api-response/chat-session-types';
import { Icon } from '@iconify/vue';
import { computed, ref, watch } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import { thoughtText } from '@/utils/luma-thoughts';

const props = withDefaults(defineProps<{
   thoughts: LumaThought[];
   /** True while the answer is still being produced. */
   live?: boolean;
   /** Shown as the live row before the first progress event arrives. */
   fallbackText?: string;
}>(), {
   live: false,
   fallbackText: '',
});

const emit = defineEmits<{
   stop: [];
}>();

/* ---------------------------------------------------------------------------------------------- */

/** Rows that are finished. While live, the newest thought is excluded — it's the active one. */
const settled = computed(() =>
   props.live ? props.thoughts.slice(0, -1) : props.thoughts,
);

/** The beat Luma is on right now, or null once the answer has landed. */
const active = computed<LumaThought | null>(() => {
   if (!props.live)
      return null;
   return props.thoughts.at(-1) ?? { stage: props.fallbackText, detail: '' };
});

/**
 * Collapsed is the resting state: once an answer exists the reasoning is noise
 * if the answer is good and diagnostic only if it isn't, so it stays folded
 * behind the summary line until asked for.
 *
 * In practice `chat-message.vue` renders the streaming and settled trails in
 * different `v-if` branches, so the settled one mounts fresh here — already
 * collapsed. The watch below covers a parent that instead keeps one instance
 * mounted and flips `live`, so the component honours its own contract however
 * it is used.
 */
const isExpanded = ref(false);

watch(() => props.live, (live, wasLive) => {
   if (wasLive && !live)
      isExpanded.value = false;
});

const stepCount = computed(() => props.thoughts.length);

/** Nothing to show before the first beat of a non-live message. */
const hasContent = computed(() => props.live || stepCount.value > 0);
</script>

<template>
   <div v-if="hasContent" class="w-full">
      <!-- Streaming: the whole trail, newest row live -->
      <template v-if="live">
         <!-- role="log" sits on the wrapper so the <ol> keeps its native list
              semantics — putting it on the list itself orphans the <li>s. -->
         <div
            v-if="settled.length"
            role="log"
            aria-live="polite"
            aria-label="Luma's progress"
         >
            <ol class="thought-list">
               <li v-for="(thought, index) in settled" :key="index" class="thought-row">
                  <span class="thought-dot" aria-hidden="true" />
                  <span class="thought-stage">{{ thought.stage }}</span>
                  <template v-if="thought.detail">
                     <span class="thought-sep" aria-hidden="true">·</span>
                     <span class="thought-detail">{{ thought.detail }}</span>
                  </template>
               </li>
            </ol>
         </div>

         <div class="flex items-center py-2">
            <div class="relative overflow-hidden flex items-center">
               <img src="@/assets/images/luma-loading.svg" alt="" class="size-10" />
               <span
                  class="capitalize font-semibold -ml-1 bg-gradient-to-r from-[#1a5456]
                  to-[#3a9a9d] bg-clip-text text-transparent animate-text-shimmer"
               >
                  {{ active ? thoughtText(active) : '' }}...
               </span>
               <!-- Shimmer overlay -->
               <div
                  class="absolute inset-0 w-1/2 animate-shimmer bg-gradient-to-r from-transparent
                  via-white/60 to-transparent pointer-events-none"
               />
            </div>
            <button
               class="ml-2 px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded-full transition-colors"
               @click="emit('stop')"
            >
               Stop
            </button>
         </div>
      </template>

      <!-- Settled: one line, expandable -->
      <template v-else>
         <button
            class="inline-flex items-center gap-1.5 py-1 text-xs text-black/45 hover:text-black/70
               transition-colors"
            :aria-expanded="isExpanded"
            @click="isExpanded = !isExpanded"
         >
            <Icon
               :icon="IconLib.actions.arrow.down"
               class="size-3.5 transition-transform"
               :class="{ '-rotate-90': !isExpanded }"
               aria-hidden="true"
            />
            Thought for {{ stepCount }} {{ stepCount === 1 ? 'step' : 'steps' }}
         </button>

         <ol v-if="isExpanded" class="thought-list mt-1 mb-2">
            <li v-for="(thought, index) in settled" :key="index" class="thought-row">
               <span class="thought-dot" aria-hidden="true" />
               <span class="thought-stage">{{ thought.stage }}</span>
               <template v-if="thought.detail">
                  <span class="thought-sep" aria-hidden="true">·</span>
                  <span class="thought-detail">{{ thought.detail }}</span>
               </template>
            </li>
         </ol>
      </template>
   </div>
</template>

<style scoped lang="postcss">
/* `animate-shimmer` is a Tailwind utility (see tailwind.config.ts) — no local
   keyframes needed. */

.thought-list {
   @apply flex flex-col gap-1 border-l border-neutral-dark/25 ml-4 pl-3 py-0.5;
}

.thought-row {
   @apply relative flex items-baseline gap-1.5 text-xs leading-relaxed [overflow-wrap:anywhere];
}

/* Sits on the rail to mark each settled beat. */
.thought-dot {
   @apply absolute -left-[17px] top-1.5 size-1.5 rounded-full bg-neutral-dark/40;
}

.thought-stage {
   @apply capitalize font-medium text-black/55 shrink-0;
}

/* A real element rather than a ::before, so the separator survives screen
   readers and copy-paste instead of running the two halves together. */
.thought-sep {
   @apply text-black/25 shrink-0;
}

.thought-detail {
   @apply text-black/40;
}
</style>
